import React, { useEffect, useMemo, useState } from 'react';
import { RadioButtonSet } from 'src/components/Quiz/RadioButtonSet';
import { getRemainingFacetsFromAnswers } from '@src/containers/QuizContainer/answerHelpers';
import { Filters as AlgoliaFilters } from 'src/clients/Algolia/constants';
import { Footer } from 'src/components/Quiz/Footer';
import { trackEvent } from 'src/services/analytics';
import { QuizQuestion } from 'src/components/Quiz/QuizQuestion';
import {
	AlertMessages,
	AnswerKeys,
	QuizAnalytics,
	ScreenProps,
	WeeklyTimeValues,
} from '@src/containers/QuizContainer/types';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { useQuiz } from '@src/components/Providers/QuizProvider';
import { Typography } from '@guildeducationinc/recess';
import styles from './WeeklyTime.module.scss';

export const values: { label: WeeklyTimeValues; disabled: boolean }[] = [
	{ label: WeeklyTimeValues.upToTwo, disabled: false },
	{ label: WeeklyTimeValues.upToSix, disabled: false },
	{ label: WeeklyTimeValues.upToTwelve, disabled: false },
	{ label: WeeklyTimeValues.upToTwentyFour, disabled: false },
	{ label: WeeklyTimeValues.moreThan24, disabled: false },
	{ label: WeeklyTimeValues.notSure, disabled: false },
];

const question = 'How much time can you set aside for class and homework each week?';

export const WeeklyTime: React.FC<ScreenProps> = props => {
	const { answers } = useQuiz();
	const { filters, index } = useSearchContext();

	const { defaultAnswers, nextScreen, previousScreen, updateAnswers } = props;
	const defaultAnswer = answers?.weeklyTime
		? answers.weeklyTime
		: defaultAnswers?.weeklyTime || undefined;
	const [weeklyTimeAnswer, setWeeklyTimeAnswer] = useState<string>(defaultAnswer);
	const [weeklyTimeValues, setWeeklyTimeValues] = useState(values);

	const analyticsData: QuizAnalytics = useMemo(() => {
		return {
			questionKey: question,
			questionType: 'radioButtons',
		};
	}, []);

	useEffect(() => {
		const availableTimeOptions = [];
		const getAvailableTimeOptions = async () => {
			const relevantAnswers = answers;

			const facets = await getRemainingFacetsFromAnswers(
				{ ...relevantAnswers, weeklyTime: '' },
				`${AlgoliaFilters.maxEffortHours}`,
				filters,
				index
			);

			facets.forEach(facet => {
				const numFacet = parseInt(facet);
				if (numFacet <= 2) {
					availableTimeOptions.push(WeeklyTimeValues.upToTwo);
				}
				if (numFacet <= 6) {
					availableTimeOptions.push(WeeklyTimeValues.upToSix);
				}
				if (numFacet <= 12) {
					availableTimeOptions.push(WeeklyTimeValues.upToTwelve);
				}
				if (numFacet <= 24) {
					availableTimeOptions.push(WeeklyTimeValues.upToTwentyFour);
				}
				if (numFacet > 24) {
					availableTimeOptions.push(WeeklyTimeValues.moreThan24);
				}
			});

			const updatedValues = values.map(value => {
				if (!availableTimeOptions.includes(value.label)) {
					value.disabled = true;
				}
				if (value.label === WeeklyTimeValues.notSure) {
					value.disabled = false;
				}
				return value;
			});
			setWeeklyTimeValues(updatedValues);
		};

		getAvailableTimeOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, index]);

	useEffect(() => {
		setWeeklyTimeAnswer(defaultAnswer);
	}, [defaultAnswer]);

	useEffect(() => {
		updateAnswers({ [AnswerKeys.weeklyTime]: weeklyTimeAnswer }, answers);
	}, [weeklyTimeAnswer, answers, updateAnswers]);

	useEffect(() => {
		trackEvent('userProgramQuizQuestionAttempted', analyticsData);
	}, [analyticsData]);

	return (
		<>
			<fieldset className={styles.container} data-testid='weekly-time'>
				<legend className={styles.title}>
					<QuizQuestion question={question} />
				</legend>
				<Typography className={styles.helperText} variant='body1'>
					Consider how much time you&apos;ll have left after work, family, friends, and other
					commitments.
				</Typography>
				<RadioButtonSet
					alertMessage={AlertMessages.weeklyTime}
					onSelect={setWeeklyTimeAnswer}
					options={weeklyTimeValues}
					value={weeklyTimeAnswer}
				/>
			</fieldset>
			<Footer
				disableNext={!weeklyTimeAnswer}
				onClickNext={() => {
					analyticsData.responseKey = weeklyTimeAnswer;
					updateAnswers({ [AnswerKeys.weeklyTime]: weeklyTimeAnswer }, answers);
					trackEvent('userProgramQuizQuestionCompleted', analyticsData);
					nextScreen();
				}}
				onClickPrevious={previousScreen}
			/>
		</>
	);
};
